import React, { useState } from 'react';
import { Card, Table, Avatar, Typography, Tag, Tabs, Row, Col } from 'antd';
import { TrophyOutlined, CrownOutlined, UserOutlined } from '@ant-design/icons';
import styled from 'styled-components';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const StyledCard = styled(Card)`
  .rank-cell {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .rank-number {
    font-weight: 700;
    font-size: 16px;
    min-width: 30px;

    &.top-1 { color: #faad14; }
    &.top-2 { color: #8c8c8c; }
    &.top-3 { color: #cd7f32; }
  }
  .user-info {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .current-user-row {
    background-color: #e6f7ff !important;
  }
  .top-three {
    margin-bottom: 24px;
  }
  .podium-item {
    text-align: center;
    padding: 16px;
    border-radius: 8px;
    background: #fafafa;

    &.first {
      background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
    }
    &.second {
      background: linear-gradient(135deg, #c0c0c0 0%, #e5e5e5 100%);
    }
    &.third {
      background: linear-gradient(135deg, #cd7f32 0%, #e8a87c 100%);
    }
  }
  .podium-rank {
    font-size: 36px;
    margin-bottom: 8px;
  }
`;

interface LeaderboardEntry {
  rank: number;
  user_id: string;
  name: string;
  points: number;
  level: number;
  achievements_count: number;
  is_current_user: boolean;
}

interface LeaderboardProps {
  leaderboard: LeaderboardEntry[];
  currentUserRank: number;
  scope: 'global' | 'regional';
  onScopeChange: (scope: 'global' | 'regional') => void;
  loading?: boolean;
}

const Leaderboard: React.FC<LeaderboardProps> = ({
  leaderboard,
  currentUserRank,
  scope,
  onScopeChange,
  loading = false
}) => {
  const topThree = leaderboard.slice(0, 3);
  const restOfLeaderboard = leaderboard.slice(3);

  const columns = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      width: 80,
      render: (rank: number) => (
        <div className="rank-cell">
          <span className={`rank-number ${rank <= 3 ? `top-${rank}` : ''}`}>
            #{rank}
          </span>
          {rank === 1 && <CrownOutlined style={{ color: '#faad14', fontSize: 20 }} />}
        </div>
      )
    },
    {
      title: 'User',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: LeaderboardEntry) => (
        <div className="user-info">
          <Avatar icon={<UserOutlined />} />
          <div>
            <Text strong={record.is_current_user}>
              {name || 'Anonymous'}
              {record.is_current_user && (
                <Tag color="blue" style={{ marginLeft: 8 }}>You</Tag>
              )}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Level {record.level}
            </Text>
          </div>
        </div>
      )
    },
    {
      title: 'Points',
      dataIndex: 'points',
      key: 'points',
      align: 'right' as 'right',
      render: (points: number) => (
        <Text strong>{points.toLocaleString()}</Text>
      )
    },
    {
      title: 'Achievements',
      dataIndex: 'achievements_count',
      key: 'achievements_count',
      align: 'center' as 'center',
      render: (count: number) => (
        <Tag color="gold">
          <TrophyOutlined /> {count}
        </Tag>
      )
    }
  ];

  return (
    <StyledCard
      bordered={false}
      loading={loading}
      title={<Title level={4} style={{ margin: 0 }}>Leaderboard</Title>}
      extra={
        <Tabs
          activeKey={scope}
          onChange={(key) => onScopeChange(key as 'global' | 'regional')}
          size="small"
        >
          <TabPane tab="Global" key="global" />
          <TabPane tab="Regional" key="regional" />
        </Tabs>
      }
    >
      {/* Top 3 Podium */}
      {topThree.length >= 3 && (
        <div className="top-three">
          <Row gutter={[16, 16]}>
            {/* 2nd Place */}
            <Col xs={8}>
              <div className="podium-item second">
                <div className="podium-rank">🥈</div>
                <Avatar size={48} icon={<UserOutlined />} />
                <div style={{ marginTop: 8 }}>
                  <Text strong>{topThree[1]?.name}</Text>
                  <br />
                  <Text type="secondary">{topThree[1]?.points.toLocaleString()} pts</Text>
                </div>
              </div>
            </Col>

            {/* 1st Place */}
            <Col xs={8}>
              <div className="podium-item first">
                <div className="podium-rank">
                  <CrownOutlined style={{ fontSize: 36, color: '#ff4d4f' }} />
                </div>
                <Avatar size={64} icon={<UserOutlined />} />
                <div style={{ marginTop: 8 }}>
                  <Text strong style={{ fontSize: 16 }}>{topThree[0]?.name}</Text>
                  <br />
                  <Text type="secondary">{topThree[0]?.points.toLocaleString()} pts</Text>
                </div>
              </div>
            </Col>

            {/* 3rd Place */}
            <Col xs={8}>
              <div className="podium-item third">
                <div className="podium-rank">🥉</div>
                <Avatar size={48} icon={<UserOutlined />} />
                <div style={{ marginTop: 8 }}>
                  <Text strong>{topThree[2]?.name}</Text>
                  <br />
                  <Text type="secondary">{topThree[2]?.points.toLocaleString()} pts</Text>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      )}

      {/* Rest of Leaderboard */}
      <Table
        columns={columns}
        dataSource={restOfLeaderboard}
        rowKey="user_id"
        pagination={{ pageSize: 10, showSizeChanger: false }}
        rowClassName={(record) => record.is_current_user ? 'current-user-row' : ''}
      />

      {currentUserRank > 10 && (
        <div style={{ marginTop: 16, padding: 16, background: '#e6f7ff', borderRadius: 8 }}>
          <Text strong>Your Rank: #{currentUserRank}</Text>
        </div>
      )}
    </StyledCard>
  );
};

export default Leaderboard;
